/**
 * Role-context client helper.
 *
 * After a successful login (email or Google OAuth), the frontend must
 * decide where to send the user: the existing Stay & Play merchant
 * portal, the new Reseller portal, or the new super-admin portal.
 *
 * The single source of truth is Tournament-API's GET /api/auth/role-context.
 * It returns { role, redirect, ...context } — we just navigate to redirect.
 *
 * The endpoint resolves role from cms_users.group_id + reseller_account_users
 * + cms_users.Courses_ID, in that priority. Documented in the backend
 * controller (resellerInviteController.getRoleContext).
 */
import { tournamentApi } from './api'

/**
 * Fetches role context. Returns the full payload (role, redirect, etc.).
 * Throws on auth failure (401) — caller should treat as "needs login".
 */
export async function fetchRoleContext() {
  const { data } = await tournamentApi.get('/auth/role-context')
  return data
}

/**
 * Decide the post-login destination, using fetchRoleContext + a fallback.
 * Falls back to /portal (the existing Stay & Play merchant landing) if
 * the call fails for non-auth reasons, so login isn't blocked by a glitch.
 */
export async function resolveLoginRedirect() {
  try {
    const ctx = await fetchRoleContext()
    return ctx?.redirect || '/portal'
  } catch (err) {
    // 401 = caller hasn't auth'd yet; let them re-login.
    if (err?.response?.status === 401) throw err
    // Anything else: degrade gracefully to the existing default.
    console.warn('[roleContext] fetch failed; defaulting to /portal', err?.message)
    return '/portal'
  }
}
